/**
 * The organisation menu under the header pill (prototype: "Your organisations").
 *
 *   Your organisations   tile · name · your role · ✓ current   (click to switch)
 *   Create organisation  only for someone who owns none (flow to come — D9 page)
 *   Join with a code     6 characters → Find → the organisation → Join (invited) or Request to join
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgs } from '@/lib/useOrgs'
import { orgApi, type FoundOrg } from '@/lib/orgApi'
import { useNotifications } from '@/lib/notifications'
import { T, mainRole } from './orgTheme'
import { OrgTile } from './OrgTile'

type Found = FoundOrg

export function OrgMenu({ anchorLeft, width, onClose }: { anchorLeft: number; width: number | string; onClose: () => void }) {
  const orgs = useOrgs()
  const navigate = useNavigate()
  const { toast } = useNotifications()
  const ref = useRef<HTMLDivElement>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [found, setFound] = useState<Found | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current && !ref.current.contains(t) && !(t as HTMLElement).closest?.('[aria-haspopup="menu"]')) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [onClose])

  const [q, setQ] = useState('')
  async function find() {
    setBusy(true); setMsg(null); setFound(null)
    try { setFound((await orgApi.findCode(code)).org) }
    catch (e) { setMsg((e as Error).message) }
    finally { setBusy(false) }
  }

  async function join() {
    if (!found) return
    setBusy(true)
    try {
      const j = await orgs.joinWithCode(code)
      toast(j.requested
        ? { status: 'info', title: `Request sent to ${j.org.name}`, body: 'An admin needs to approve it. You’ll get an email when they do.' }
        : { status: 'success', title: `You joined ${j.org.name}` })
      onClose()
    } catch (e) { setMsg((e as Error).message); setFound(null) }
    finally { setBusy(false) }
  }

  return (
    <div ref={ref} role="menu"
      style={{ position: 'absolute', top: 52, left: anchorLeft, width, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: '0 16px 40px rgba(28,26,34,.16)', padding: 8, zIndex: 40 }}>
      <div style={{ padding: '8px 10px 6px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: T.faint }}>Your organisations</div>
      {/* Taller, and searchable past 6 (7 Oct 2026): a 280px box fitted exactly five, so a
          sixth organisation sat below the fold with no scrollbar showing on a Mac. */}
      {orgs.orgs.length > 6 && (
        <div style={{ padding: '2px 4px 6px' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search organisations" aria-label="Search organisations"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13.5, border: `1px solid ${T.border}`, borderRadius: 8, outline: 'none' }} />
        </div>
      )}
      <div style={{ maxHeight: 'min(60vh, 480px)', overflowY: 'auto' }}>
        {orgs.orgs.filter(o => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase())).map(o => {
          const on = o.id === orgs.current?.id
          return (
            // Switching goes to that organisation's Home (22 Oct 2026) — the page you were on (a course, a
            // group, a member) belongs to the organisation you left. Home first, so that page never
            // reloads against the new one. Picking the current one just closes the menu.
            <button key={o.id} role="menuitem" onClick={() => { onClose(); if (on) return; navigate('/'); void orgs.switchTo(o.id) }}
              className="hover:bg-[#FAF6FF]"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 9, border: 'none', background: on ? T.tintSoft : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
              <OrgTile id={o.id} name={o.name} logoUrl={o.logoUrl} size={30} radius={7} fontSize={11.5} />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: T.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</span>
                <span style={{ fontSize: 12.5, color: T.faint }}>{mainRole(o.roles)}</span>
              </span>
              <span style={{ color: T.purple, fontSize: 15, width: 16 }}>{on ? '✓' : ''}</span>
            </button>
          )
        })}
      </div>

      {!orgs.ownsOne && (
        <button role="menuitem" onClick={() => { onClose(); navigate('/org/new') }}
          className="hover:bg-[#FAF6FF]"
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 9, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', marginTop: 2 }}>
          <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 7, background: T.hover, color: T.ink, display: 'grid', placeItems: 'center', fontSize: 16 }}>+</span>
          <span style={{ fontSize: 14, fontWeight: 500, color: T.dark }}>Create organisation</span>
        </button>
      )}

      <div style={{ borderTop: `1px solid ${T.border}`, margin: '8px 2px 0', padding: '12px 8px 6px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>Join with an organisation code</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={code} onChange={e => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)); setMsg(null); setFound(null) }}
            onKeyDown={e => e.key === 'Enter' && code.length === 6 && void find()}
            placeholder="6-character code" aria-label="Organisation code"
            style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: `1px solid ${T.inputBorder}`, borderRadius: 8, fontSize: 14, fontFamily: 'ui-monospace, monospace', letterSpacing: '.12em', outline: 'none' }} />
          <button onClick={() => void find()} disabled={code.length !== 6 || busy}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.inputBorder}`, background: '#fff', fontSize: 13.5, fontWeight: 500, cursor: 'pointer', opacity: code.length !== 6 || busy ? 0.5 : 1 }}>{busy && !found ? 'Finding…' : 'Find'}</button>
        </div>
        {msg && <span style={{ fontSize: 12.5, color: T.red }}>{msg}</span>}
        {found && (() => {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: `1px solid ${T.border}`, borderRadius: 10 }}>
              <OrgTile id={found.id} name={found.name} logoUrl={found.logoUrl} size={30} radius={7} fontSize={11.5} />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.dark }}>{found.name}</span>
                <span style={{ fontSize: 12.5, color: T.faint }}>
                  {/* No member count (21 Oct 2026) — how big an organisation is isn't a stranger's business. */}{found.invited ? 'You’re invited' : found.requested ? 'Request sent' : 'An admin approves new members'}
                </span>
              </span>
              {found.requested
                ? <span style={{ fontSize: 13, color: T.muted, whiteSpace: 'nowrap' }}>Waiting</span>
                : <button onClick={() => void join()} disabled={busy}
                    style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: T.purple, color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap', opacity: busy ? 0.7 : 1 }}>
                    {/* Shows it's working (21 Oct 2026) — the request takes a moment and the button gave no sign. */}
                    {found.invited ? (busy ? 'Joining…' : 'Join') : (busy ? 'Sending…' : 'Request')}</button>}
            </div>
          )
        })()}
      </div>
    </div>
  )
}
